/**
 * 国家趋势图子组件（带国旗图例）
 * 对应板块: 板块 E — 在投广告数分国家
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_V, DBO_TM, DBO_U } from '@op/utils/format';
import { chartAllColors } from '@op/constants';
import { lineIcon, barIcon, pieIcon, tableIcon } from '@op/constants/chart';
import { getCountryFlag } from '@/pages/home/_config/countries';
import useLanguage from '@op/hooks/useLanguage';
import { echartsContent } from '@op/utils';
import type { ICountryTrendChartProps, TChartType } from './types';
import {
  CROSS_METRIC_TEXT_MAP,
  getCountryDisplayName,
  normalizeCountryCode,
  parseCountrySeriesKey,
} from './types';
import CardCommon from '@op/components/CardCommon';
import { dealWithOthersName } from '@op/utils/languageValue';
import { getChartMetricLabelKey } from '../../utils/metricLabels';

const CHART_OPERATIONS_DEFAULT = [lineIcon, barIcon, pieIcon, tableIcon];
const CHART_OPERATIONS_CROSS = [lineIcon, tableIcon];

const CountryTrendChart: React.FC<ICountryTrendChartProps> = ({
  title,
  data,
  loading,
  crossAnalysis,
  pieData,
  pieDataDate,
  filter,
  chartType,
  setChartType
}) => {
  const { formatMessage, isEn } = useLanguage();
  const eChartsRef = useRef<any>(null);

  const getSeriesMeta = useCallback(
    (seriesName: string, options?: { isPrimary?: boolean; }) => {
      const { countryCode, metric } = parseCountrySeriesKey(seriesName);
      const resolvedCountryCode = countryCode || seriesName;
      const alpha2Code = normalizeCountryCode(resolvedCountryCode);
      const countryName = alpha2Code
        ? getCountryDisplayName(resolvedCountryCode, isEn)
        : dealWithOthersName(resolvedCountryCode);
      const metricText = metric ? formatMessage(CROSS_METRIC_TEXT_MAP[metric]) : '';

      // 交叉分析模式：primary series 显示「国家名 在投广告数」, secondary 显示「国家名 MetricLabel」
      let displayName = countryName;
      if (crossAnalysis?.enabled) {
        if (options?.isPrimary) {
          const crossCountryAlpha2 = crossAnalysis.country
            ? normalizeCountryCode(crossAnalysis.country)
            : alpha2Code;
          const crossCountryName = crossCountryAlpha2
            ? getCountryDisplayName(crossAnalysis.country || resolvedCountryCode, isEn)
            : countryName;
          displayName = `${crossCountryName} ${formatMessage(getChartMetricLabelKey('Active Ads', filter))}`;
          return { alpha2Code: crossCountryAlpha2 || alpha2Code, metric, displayName };
        }
        if (crossAnalysis.metric) {
          const crossAlpha2 = crossAnalysis.country
            ? normalizeCountryCode(crossAnalysis.country)
            : '';
          const crossName = crossAlpha2
            ? getCountryDisplayName(crossAnalysis.country || '', isEn)
            : '';
          const crossMetricLabel = formatMessage(getChartMetricLabelKey(CROSS_METRIC_TEXT_MAP[crossAnalysis.metric], filter));
          displayName = crossName
            ? `${crossName} ${crossMetricLabel}`
            : metricText || seriesName;
          return { alpha2Code: crossAlpha2, metric: crossAnalysis.metric, displayName };
        }
      }

      if (metricText) {
        displayName = `${countryName} ${metricText}`;
      }

      return { alpha2Code, metric, displayName };
    },
    [formatMessage, crossAnalysis, isEn, filter],
  );

  // 主数据 + 交叉分析数据已在 useTimelineData hook 中合并到 data.series 中（compare 部分带 isCompare 标记）
  // 这里直接读取 data.series，无需再用 useMemo 合并 primary/secondary，避免首帧错位与多余渲染
  const allSeries = useMemo(
    () =>
      (data?.series || []) as Array<{
        name: string;
        data: number[];
        isCompare?: boolean;
      }>,
    [data?.series],
  );
  const hasSecondaryAxis = !!(
    crossAnalysis?.enabled && allSeries.some(s => s.isCompare)
  );

  const tableData = useMemo(() => {
    if (chartType !== 'table') return null;
    if (!data?.xAxis?.length || !allSeries.length) return [];

    return data.xAxis.map((time, index) => {
      const row: Record<string, any> = { time };
      allSeries.forEach((seriesItem) => {
        row[seriesItem.name] = seriesItem.data[index];
      });
      return row;
    });
  }, [chartType, allSeries, data?.xAxis]);

  const tableColumns = useMemo(() => {
    if (!tableData?.length) return [];

    return [
      {
        header: formatMessage('Time'),
        key: 'time',
        align: 'left',
        width: filter?.date_type === 'weekly' ? 220 : 160,
        fixed: 'left' as const,
        render: (cvm: any) => DBO_EC.toolTime(cvm.time, filter?.date_type || 'daily'),
      },
      ...allSeries.map((seriesItem) => {
        const isPrimary = !seriesItem.isCompare;
        const meta = getSeriesMeta(seriesItem.name, { isPrimary });
        return {
          header: meta.alpha2Code ? (
            <span className="dbo-country-trend-chart__table-header">
              <img src={getCountryFlag(meta.alpha2Code)} alt={meta.displayName} />
              <span>{meta.displayName}</span>
            </span>
          ) : (
            dealWithOthersName(meta.displayName)
          ),
          key: seriesItem.name,
          align: 'right' as const,
          width: 200,
          render: (cvm: any) => DBO_V.reviews(cvm[seriesItem.name]),
        };
      }),
    ];
  }, [allSeries, formatMessage, getSeriesMeta, tableData]);

  // ── split useMemo per chart family ──────────────────────────────────────────
  // 拆分原因：
  //   - line/bar 模式下 pieData / pieDataDate 变化不再触发 line-bar 重算
  //   - pie 模式下 data / combinedSeries / crossAnalysis* 变化不再触发 pie 重算
  // 每个 useMemo 只列出对应分支实际读取的 props/state，避免跨模式 dep 串扰

  const lineBarOptions = useMemo(() => {
    if (!data?.xAxis?.length || !allSeries.length) return null;

    const isBarMode = chartType === 'bar';

    const series = allSeries.map((seriesItem, index) => {
      const isSecondarySeries = !!seriesItem.isCompare;
      const color = chartAllColors[index % chartAllColors.length];
      const meta = getSeriesMeta(seriesItem.name, { isPrimary: !isSecondarySeries });

      let lineStyle: any;
      if (!isBarMode) {
        lineStyle = isSecondarySeries
          ? { color, width: 3, type: 'dashed' as const }
          : { color, width: 2 };
      }

      return {
        name: meta.displayName,
        type: isBarMode ? 'bar' : 'line',
        yAxisIndex: isSecondarySeries ? 1 : 0,
        data: seriesItem.data.map((v: number) => v || undefined),
        ...(isBarMode ? { stack: 'total', barMaxWidth: 60 } : {}),
        itemStyle: { color },
        lineStyle,
      };
    });

    return {
      tooltip: {
        formatter: (params: any[]) => {
          return formatChartParamsDate({
            title: DBO_EC.toolTime(params[0]?.axisValue, filter?.date_type || 'daily') || '',
            params,
            listTitleFunc: (item: any) => {
              const matched = allSeries.find((s) => {
                const m = getSeriesMeta(s.name, { isPrimary: !s.isCompare });
                return m.displayName === item.seriesName;
              });
              const isPrimary = matched ? !matched.isCompare : true;
              const meta = getSeriesMeta(
                matched?.name || item.seriesName,
                { isPrimary },
              );
              if (meta.alpha2Code) {
                const flagUrl = getCountryFlag(meta.alpha2Code);
                return `<span class="dbo-chart-formatter-img"><img src=${flagUrl} class="chart-common-formatter-img" /> ${meta.displayName}</span>`;
              }
              return dealWithOthersName(meta.displayName);
            },
            dealwithRecords: crossAnalysis?.enabled
              ? undefined
              : (records: any[], total: number) => [
                { text: title, value: DBO_V.reviews(total) },
                ...records,
              ],
          });
        },
      },
      legend: {
        formatter: (name: string) => {
          const matched = allSeries.find((seriesItem) => {
            const isPrimary = !seriesItem.isCompare;
            return getSeriesMeta(seriesItem.name, { isPrimary }).displayName === name;
          });
          if (!matched) return name;
          const meta = getSeriesMeta(matched.name, {
            isPrimary: !matched.isCompare,
          });
          return meta.alpha2Code ? `{flag_${meta.alpha2Code}|} ${name}` : name;
        },
        textStyle: {
          rich: allSeries.reduce(
            (acc: Record<string, any>, seriesItem) => {
              const isPrimary = !seriesItem.isCompare;
              const meta = getSeriesMeta(seriesItem.name, { isPrimary });
              if (meta.alpha2Code) {
                acc[`flag_${meta.alpha2Code}`] = {
                  width: 16,
                  height: 12,
                  backgroundColor: { image: getCountryFlag(meta.alpha2Code) },
                };
              }
              return acc;
            },
            {},
          ),
        },
      },
      xAxis: {
        type: 'category',
        data: data.xAxis || [],
        axisLabel: { color: '#838697', fontSize: 12, formatter: (value: string) => DBO_EC.timeX(value, filter?.date_type || 'daily') },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: { color: '#838697', formatter: (v: any) => echartsContent(v) },
          nameTextStyle: { color: '#444444', fontWeight: 400, fontSize: '12px' },
          splitNumber: 5,
          splitLine: { lineStyle: { color: '#E5E5E5', type: 'dashed' } },
        },
        ...(hasSecondaryAxis
          ? [
            {
              type: 'value',
              position: 'right',
              axisLabel: { color: '#838697', formatter: (v: any) => echartsContent(v) },
              splitLine: { show: false },
            },
          ]
          : []),
      ],
      series,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allSeries,
    crossAnalysis,
    title,
    getSeriesMeta,
    filter,
    chartType,
    hasSecondaryAxis,
    data?.xAxis,
  ]);

  const pieOptions = useMemo(() => {
    const { series, totalSeries: pieTotalData = 0, isEmpty } = pieData || {};
    if (isEmpty) return null;
    const pieSeries = series?.map((item) => {
      const meta = getSeriesMeta(item.name);
      return {
        name: meta.displayName,
        value: item.value,
        dimensionValue: item.name,
      };
    });


    const flagRichMap = pieSeries?.reduce((acc: Record<string, any>, item) => {
      const alpha2Code = normalizeCountryCode(item.dimensionValue);
      if (alpha2Code) {
        acc[`flag_${alpha2Code}`] = {
          width: 16,
          height: 12,
          backgroundColor: { image: getCountryFlag(alpha2Code) },
        };
      }
      return acc;
    }, {});

    return {
      series: {
        data: pieSeries?.map(({ name, value }) => ({ name, value })),
        type: 'pie',
        label: {
          color: '#262832',
          alignTo: 'edge',
          edgeDistance: 10,
          formatter: (params: any) => {
            const percent = DBO_V.percentage(params?.value, pieTotalData, 2);
            const alpha2Code = normalizeCountryCode(
              pieSeries?.find((d) => getSeriesMeta(d.dimensionValue).displayName === params.name)
                ?.dimensionValue || '',
            );
            const flagIcon = alpha2Code ? `{flag_${alpha2Code}|}` : '';
            const percentText = params?.value
              ? `${DBO_V.reviews(params.value)}(${percent})`
              : DBO_V.reviews(params.value);
            return `${flagIcon}{value| ${params.name}}: {a|${percentText}}\n{hrLine|}\n{a|}`;
          },
          rich: {
            a: { lineHeight: 20, align: 'center' },
            hrLine: { borderColor: 'auto', width: '105%', borderWidth: 0.5, height: 0.5 },
            ...flagRichMap,
          },
        },
        labelLayout: (params: any) => {
          const isLeft =
            params.labelRect.x <
            eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
          const points = params.labelLinePoints;
          points[2][0] = isLeft
            ? params.labelRect.x
            : params.labelRect.x + params.labelRect.width;
          return { labelLinePoints: points };
        },
        emphasis: {
          focus: 'self',
          label: { opacity: 0.2 },
          labelLine: { lineStyle: { opacity: 0.2 } },
        },
        blur: {
          label: { opacity: 0.2 },
          itemStyle: { opacity: 0.2 },
          labelLine: { lineStyle: { opacity: 0.2 } },
        },
      },
      legend: {
        formatter: (name: string) => {
          const matched = pieSeries?.find((item) => item.name === name);
          const alpha2Code = normalizeCountryCode(matched?.dimensionValue || '');
          return alpha2Code ? `{flag_${alpha2Code}|} ${name}` : name;
        },
        textStyle: {
          rich: flagRichMap,
        },
      },
      tooltip: {
        borderWidth: 0,
        borderRadius: 6,
        padding: 0,
        formatter: (params: any) => {
          const alpha2Code = normalizeCountryCode(
            pieSeries?.find((d) => getSeriesMeta(d.dimensionValue).displayName === params.name)
              ?.dimensionValue || '',
          );
          const datePrefix = pieDataDate || '';
          const flagImg = alpha2Code
            ? `<img src="${getCountryFlag(alpha2Code)}" class="chart-common-formatter-img" style="width:16px;height:12px;margin-right:4px;" />`
            : '';
          return formatChartParamsDate({
            params,
            tablesConfigFunc: () => [
              {
                records: [
                  {
                    text: formatMessage(getChartMetricLabelKey('Active Ads', filter)),
                    value: String(DBO_V.reviews(params.value || 0)),
                  },
                ],
                columns: [
                  { key: 'text', header: datePrefix },
                  { key: 'value', align: 'right', weight: 'bold', header: `${flagImg}${params.name}` },
                ],
              },
            ],
          });
        },
      },
      title: {
        text: `{name|${DBO_V.reviews(pieTotalData)}}`,
        top: '43%',
        left: 'center',
        textStyle: {
          rich: {
            name: { fontSize: 24, fontFamily: 'num-m', color: '#000000', padding: [10, 0] },
          },
        },
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '34%',
          style: { fill: '#444444', fontSize: 12 },
        },
      ],
    };
  }, [pieData, pieDataDate, getSeriesMeta, filter, formatMessage]);

  const options = chartType === 'pie' ? (pieOptions ?? {}) : (lineBarOptions ?? {});

  // 交叉分析时右侧 Y 轴标题
  const rightTitle = useMemo(() => {
    if (!crossAnalysis?.enabled || !crossAnalysis.metric || !hasSecondaryAxis) {
      return undefined;
    }
    return formatMessage(getChartMetricLabelKey(CROSS_METRIC_TEXT_MAP[crossAnalysis.metric], filter));
  }, [crossAnalysis, hasSecondaryAxis, formatMessage, filter]);

  // ==================== 下载 ====================
  const handleDownload = useCallback(() => {
    if (!data?.xAxis?.length || !allSeries.length) return;

    const flatData = data.xAxis.map((time: string, index: number) => {
      const row: Record<string, any> = { time };
      allSeries.forEach((seriesItem) => {
        row[seriesItem.name] = seriesItem.data[index];
      });
      return row;
    });

    const legend = allSeries.map((s) => s.name);
    const columnHeaders = Object.fromEntries(
      allSeries.map((seriesItem) => {
        const isPrimary = !seriesItem.isCompare;
        const meta = getSeriesMeta(seriesItem.name, { isPrimary });
        return [seriesItem.name, meta.displayName];
      }),
    );

    DBO_U.download({
      key: 'AdsOverviewTimeline',
      fileName: 'Ad Creatives Country Analysis',
      filter,
      data: flatData,
      auxiliaryParams: { legend, columnHeaders },
    });
  }, [allSeries, data, filter, getSeriesMeta]);

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <CardCommon
      className="dbo-country-trend-chart"
      defaultOperation={chartType}
      operations={crossAnalysis?.enabled ? CHART_OPERATIONS_CROSS : CHART_OPERATIONS_DEFAULT}
      hasDownload
      handleDownloadOnClick={handleDownload}
      contentCard
      operationInfo={[chartType, setChartType]}
    >
      {
        chartType === 'table' ? (
          <TableCommonHook
            recordKey="time"
            columns={tableColumns as any[]}
            records={tableData as any[]}
            hasPageable={false}
            maxHeight={352.5 - 50.5}
            bordered
            minWidth="auto"
            className="dbo-country-trend-chart-table"
          />
        ) : (
          <EChartCommon
            ref={eChartsRef}
            optionsProps={{ type: chartType }}
            options={options}
            height={352.5}
            empty={chartType === 'pie' ? pieData?.isEmpty : !allSeries.length}
            loading={loading}
            leftTitle={crossAnalysis?.enabled ? title : undefined}
            rightTitle={crossAnalysis?.enabled ? rightTitle : undefined}
            emptyHeight={352.5}
          />
        )
      }
    </CardCommon>
  );
};

export default React.memo(CountryTrendChart);
